import React from "react";
import {Button, Table} from "react-bootstrap";
import '../CommonCss/TableSmall.css'
import {saveAs} from "file-saver";
import {
    ColumnDef,
    ColumnFiltersState,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    useReactTable,
} from "@tanstack/react-table";

export interface SangerQCReportCSVTableRow {
    cloneId: string,
    label: string,
    sgA: string,
    sgB: string,
    cloneConstruct: string,
    sgAId: string,
    sgBId: string,
    sgALength: number,
    sgBLength: number,
    clone: string,
    hU6: string,
    scf1: string,
    mU6: string,
    scf4: string,
    finalLabel: string,
    spacer: string,
}

export const TanStackSangerQCReportTable: React.FC<{
    data: SangerQCReportCSVTableRow[];
    columns: ColumnDef<SangerQCReportCSVTableRow, any>[];
}> = ({data, columns}) => {
    const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([])

    const table = useReactTable({
        data,
        columns,
        state: {
            columnFilters,
        },
        onColumnFiltersChange: setColumnFilters,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
    })

    const handleExportCSV = () => {
        const visibleCols = table.getVisibleFlatColumns().map(col => col.id)
        const csvRows = [visibleCols.join(',')]

        table.getFilteredRowModel().rows.forEach(row => {
            const values = row.getVisibleCells().map(cell => JSON.stringify(cell.getValue() ?? ''))
            csvRows.push(values.join(','))
        })

        const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8' })
        saveAs(blob, 'sanger_qc_report.csv')
    }

    return (
        <>
            <div className="mb-3 text-end">
                <Button
                    className="btn-success btn-sm"
                    onClick={handleExportCSV}
                >
                    Export to CSV
                </Button>
            </div>

            <div className="table-responsive">
                <Table striped bordered hover className="table-small-text">
                    <thead>
                    {table.getHeaderGroups().map(headerGroup => (
                        <tr key={headerGroup.id}>
                            {headerGroup.headers.map(header => {
                                // Skip hidden columns
                                if (header.column.columnDef.meta?.hidden) return null;

                                return (
                                    <th key={header.id}>
                                        {flexRender(header.column.columnDef.header, header.getContext())}

                                        {/* Render filter component if column has filtering enabled */}
                                        {header.column.getCanFilter() && (
                                            <div>
                                                {header.column.columnDef.meta?.filterComponent ? (
                                                    header.column.columnDef.meta.filterComponent({ column: header.column })
                                                ) : (
                                                    <input
                                                        type="text"
                                                        value={(header.column.getFilterValue() as string) ?? ''}
                                                        onChange={e => header.column.setFilterValue(e.target.value)}
                                                        placeholder="Filter..."
                                                        className="form-control form-control-sm table-small-text-filter"
                                                    />
                                                )}
                                            </div>
                                        )}
                                    </th>
                                );
                            })}
                        </tr>
                    ))}
                    </thead>
                    <tbody>
                    {table.getRowModel().rows.map(row => (
                        <tr key={row.id}>
                            {row.getVisibleCells().map(cell => {
                                // Skip hidden columns
                                if (cell.column.columnDef.meta?.hidden) return null;

                                return (
                                    <td key={cell.id}>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                    {table.getRowModel().rows.length === 0 && (
                        <tr>
                            <td colSpan={columns.filter(col => !col.meta?.hidden).length}>
                                <div className="text-center p-3">No data found</div>
                            </td>
                        </tr>
                    )}
                    </tbody>
                </Table>
            </div>
        </>
    )
}